import { useEffect, useRef } from 'react';

/**
 * Keeps a list page live without manual reloads: re-runs `refresh` on an
 * interval (default 20s), skips ticks while the tab is hidden (no wasted
 * requests), and refreshes immediately when the user returns to the tab.
 *
 * Usage:  useAutoRefresh(load);            // 20s cadence
 *         useAutoRefresh(load, 60000);     // custom cadence
 *
 * `refresh` should be a stable callback (useCallback) that re-fetches the
 * page's data silently — the caller keeps showing stale rows during the
 * re-fetch rather than flashing a spinner.
 */
export default function useAutoRefresh(refresh, intervalMs = 20000) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') refreshRef.current();
    };
    const timer = setInterval(tick, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [intervalMs]);
}
